import * as React from 'react';
import { ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/shadcn/popover';
import { ClearButton } from '../ClearButton';
import { ChevronDownIcon } from '../icons/Chevron';
import { SelectMenu } from './SelectMenu';
import {
  selectLabelVariants,
  selectLabelTextVariants,
  selectTriggerVariants,
  selectContentVariants,
  selectTextVariants,
  selectIconsWrapperVariants,
  selectIconVariants,
  SELECT_STATE,
} from './styles';

// ============================================
// Select Context
// ============================================

export interface SelectContextValue {
  /** Currently selected value (option identifier) */
  selectedValue?: string;
  /** Whether the parent Select has onChange (enables value-based mode) */
  hasOnChange: boolean;
  /** Called by SelectOption when clicked — passes option value */
  onSelect: (value: string) => void;
  /** Called by SelectOption on mount to register its label for display resolution */
  registerLabel: (value: string, label: React.ReactNode) => void;
}

export const SelectContext = React.createContext<SelectContextValue | null>(null);

export interface SelectProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * Label text for the select
   */
  label?: string;
  /**
   * Selected value text (legacy) or selected option identifier (when onChange is provided).
   *
   * - Without `onChange`: treated as the display text directly.
   * - With `onChange`: treated as the selected option's `value` prop;
   *   the display text is auto-resolved from the matching SelectOption's children.
   */
  value?: string;
  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;
  /**
   * Whether the select is disabled
   */
  disabled?: boolean;
  /**
   * Change handler called with the selected option's value.
   * When provided, `value` is treated as the selected option's identifier
   * and the display text is auto-resolved from matching SelectOption children.
   */
  onChange?: (value: string) => void;
  /**
   * Clear button click handler. When provided and value exists, clear button is shown.
   */
  onClear?: () => void;
  /**
   * Dropdown menu content (SelectOption elements)
   */
  children?: ReactNode;
  /**
   * Controlled open state (optional — uncontrolled by default)
   */
  open?: boolean;
  /**
   * Callback when open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Popover alignment
   */
  align?: 'start' | 'center' | 'end';
  /**
   * ID for testing
   */
  dataTestId?: string;
}

export const Select = React.forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      label,
      value,
      placeholder = 'Select option',
      disabled = false,
      onChange,
      onClear,
      children,
      open: controlledOpen,
      onOpenChange,
      align = 'start',
      className,
      dataTestId = 'select',
      ...props
    },
    ref
  ) => {
    const [internalOpen, setInternalOpen] = React.useState(false);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;

    // Label registry: maps option value → display label (ReactNode)
    const labelRegistryRef = React.useRef(new Map<string, React.ReactNode>());
    const [registryVersion, setRegistryVersion] = React.useState(0);

    const registerLabel = React.useCallback((optValue: string, optLabel: React.ReactNode) => {
      const prev = labelRegistryRef.current.get(optValue);
      labelRegistryRef.current.set(optValue, optLabel);
      // Only trigger re-render if the label for the current value changed
      if (prev !== optLabel) {
        setRegistryVersion((v) => v + 1);
      }
    }, []);

    const handleOpenChange = (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    };

    // Called by SelectOption via context when an option is selected
    const handleSelect = React.useCallback((optValue: string) => {
      onChange?.(optValue);
      handleOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onChange]);

    // Resolve display text:
    // - With onChange: look up label from registry by value
    // - Without onChange: use value directly (legacy behavior)
    const resolvedDisplayText = React.useMemo(() => {
      if (!onChange) return value;
      if (!value) return undefined;
      return labelRegistryRef.current.get(value) ?? value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onChange, value, registryVersion]);

    const isFilled = !!value;
    const finalState = disabled ? SELECT_STATE.DISABLED : isFilled ? SELECT_STATE.FILLED : SELECT_STATE.DEFAULT;

    const handleTriggerClick = () => {
      if (disabled) return;
      handleOpenChange(!open);
    };

    const handleClear = () => {
      if (disabled) return;
      onClear?.();
    };

    const handleOptionClick = (e: React.MouseEvent) => {
      // Only auto-close for legacy mode (no onChange)
      // When onChange is provided, closing is handled by handleSelect
      if (onChange) return;
      const target = e.target as HTMLElement;
      if (target.closest('[role="option"]')) {
        handleOpenChange(false);
      }
    };

    const contextValue = React.useMemo<SelectContextValue>(() => ({
      selectedValue: onChange ? value : undefined,
      hasOnChange: !!onChange,
      onSelect: handleSelect,
      registerLabel,
    }), [onChange, value, handleSelect, registerLabel]);

    return (
      <SelectContext.Provider value={contextValue}>
        <div ref={ref} className={cn('flex flex-col gap-3xs w-full', className)} {...props}>
          {label && (
            <div className={cn(selectLabelVariants())}>
              <label className={cn(selectLabelTextVariants())}>{label}</label>
            </div>
          )}

          {/* Hidden mount to register SelectOption labels before popover opens */}
          {onChange && <div hidden>{children}</div>}

          <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <div
                className={cn(selectTriggerVariants({ state: finalState, open }))}
                onClick={handleTriggerClick}
                data-testid={dataTestId}>
                <div className={cn(selectContentVariants())}>
                  <div className={cn(selectTextVariants({ filled: isFilled }))}>
                    {resolvedDisplayText || placeholder}
                  </div>
                </div>

                <div className={cn(selectIconsWrapperVariants())}>
                  {onClear && isFilled && !disabled && (
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                    <div onClick={(e) => e.stopPropagation()}>
                      <ClearButton
                        onClick={handleClear}
                        dataTestId={`${dataTestId}-clear`}
                      />
                    </div>
                  )}
                  <div className={cn(selectIconVariants({ open }))} data-testid={`${dataTestId}-chevron`}>
                    <ChevronDownIcon />
                  </div>
                </div>
              </div>
            </PopoverTrigger>

            {children && (
              <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-none border-none shadow-none bg-transparent"
                align={align}
                sideOffset={4}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div onClick={handleOptionClick}>
                  <SelectMenu>
                    {children}
                  </SelectMenu>
                </div>
              </PopoverContent>
            )}
          </Popover>
        </div>
      </SelectContext.Provider>
    );
  }
);

Select.displayName = 'Select';
